import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as fabric from "fabric";
import { useLocation } from "react-router-dom";
import "./EditImage.css";
import Sidebar from "../../component/ImageEditor/SideBar";
import { toPng } from "html-to-image";
import ImageEditorLoader from "../../component/Loader/ImageEditorLoader";
import { shapes } from "../../component/ImageEditor/Shapes";
import CustomFrames from "../../component/ImageEditor/CustomFrames";
import { jsPDF } from "jspdf";
import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DargableLogo from "../../component/ImageEditor/DargableLogo";
import { CloseSharp, CropSquareSharp } from "@mui/icons-material";

const ImageEditor = () => {
  const isMobile = useMediaQuery("(max-width:1450px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [preview, setPreview] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [leagendUrl, setLeagendUrl] = useState("");
  const [showShapeSelector, setShowShapeSelector] = useState(false);
  const [activeLogo, setActiveLogo] = useState("");
  const [uploadedLogos, setUploadedLogos] = useState([]);
  const [frameSize, setFrameSize] = useState(200);
  const [isLoading, setIsLoading] = useState(false);
  const [isLeagend, setIsLeagend] = useState(false);
  const [exportFormat, setExportFormat] = useState("png");

  const [selectedTextProps, setSelectedTextProps] = useState({
    fill: "#808080",
    fontSize: 24,
    fontWeight: "normal",
    fontFamily: "Arial",
    textBoxFillColor: "#808080",
    offsetX: 10,
    offsetY: 10,
    blur: 3,
    textBoxFillColorTransparent: true,
    textBoxOpacity: 50,
    isDropShadowTransparent: null,
    textBoxBorderColor: "#000000",
    textBoxBorderColorTransparent: true,
    textBoxBorderWidth: 2,
  });

  const [selectedShapeProps, setSelectedShapeProps] = useState({
    fill: "#000000",
    stroke: "#000000",
    strokeWidth: 1,
    scale: 1,
    isDropShadowTransparent: null,
    opacity: 100,
  });

  const [frameText, setFrameText] = useState({
    lat: 24.9409607,
    lon: 67.1527284,
    title: "Choose title",
    subTitle: "Choose sub title",
  });

  const [frameDesign, setFrameDesign] = useState({
    textColor: { r: 192, g: 189, b: 189, a: 1 },
    frameColor: "#000000",
    frameText: "",
    frameStyle: "",
  });

  const [selectedObjectScale, setSelectedObjectScale] = useState({
    scale: null,
    scaleX: 0,
    scaleY: 0,
  });

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orientation = queryParams.get("orientation");

  const svgInputRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricCanvas = useRef(null);
  const frameRef = useRef(null);

  // --------------------- USE EFFECTS ---------------------

  useLayoutEffect(() => {
    // Init Fabric Canvas only once
    if (fabricCanvas.current) {
      fabricCanvas.current.dispose();
    }

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 800,
      height: 400,
      backgroundColor: "#f3f3f3",
    });

    fabricCanvas.current = canvas;

    return () => {
      canvas.dispose();
    };
  }, []);

  useLayoutEffect(() => {
    const stateString = queryParams.get("dataUrl");
    const leagendUrl = queryParams.get("leagendUrl");
    console.log(leagendUrl, "Leagend url");

    const loadImages = async () => {
      if (stateString) {
        try {
          await handleFabricImageUpload(stateString); // Wait for background
          setImageUrl(stateString);

          if (leagendUrl) {
            setLeagendUrl(leagendUrl);
            await handleFabricImageUpload(leagendUrl, "legendImage"); // Overlay after
          }
        } catch (err) {
          console.error("Error loading images:", err);
        }
      }
    };

    loadImages();

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [location.search]);

  useEffect(() => {
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    const updateSelectedProps = () => {
      const active = canvas.getActiveObject();

      if (
        (!active.id || active.id !== "backgroundImage") &&
        active?.type !== "textbox"
      ) {
        setSelectedObjectScale({
          scale: active?.scaleX || 1,
          scaleX: active?.scaleX || 1,
          scaleY: active?.scaleY || 1,
        });
      } else {
        setSelectedObjectScale({ scale: null, scaleX: 0, scaleY: 0 });
      }
      if (active?.type === "textbox") {
        setSelectedTextProps({
          fill: active.fill || "#808080",
          fontSize: active.fontSize || 24,
          fontWeight: active.fontWeight || "normal",
          fontFamily: active.fontFamily || "Arial",
          textBoxFillColor: active.textBoxFillColor || "#808080",
          textBoxFillColorTransparent: true,
          offsetX: active?.shadow?.offsetX || 10,
          offsetY: active?.shadow?.offsetY || 10,
          blur: active?.shadow?.blur || 3,
          textBoxOpacity: 50,
          isDropShadowTransparent:
            active?.shadow?.color == "rgba(0,0,0,0.5)" || null,
          textBoxBorderColor: active.textBoxBorderColor || "#000000",
          textBoxBorderColorTransparent: true,
          textBoxBorderWidth: active.textBoxBorderWidth || 2,
        });
        setSelectedShapeProps(null);
      } else if (active?.type !== "image" && active?.type !== "group") {
        // For shapes (groups, paths, rect, circle, etc.)

        setSelectedShapeProps({
          fill: active.fill || "#808080",
          stroke: active.stroke || "#000000",
          strokeWidth: active.strokeWidth || 1,
          isDropShadowTransparent: active?.shadow?.color || null,
          opacity: active.opacity * 100 || 100,
          offsetX: active?.shadow?.offsetX || 10,
          offsetY: active?.shadow?.offsetY || 10,
          scale: active.scaleX || 1,
        });
        setSelectedTextProps(null);
      } else {
        resetState();
      }
    };
    console.log("print effect");
    canvas.on("selection:created", updateSelectedProps);
    canvas.on("selection:updated", updateSelectedProps);
    canvas.on("selection:cleared", resetState);

    return () => {
      canvas.off("selection:created", updateSelectedProps);
      canvas.off("selection:updated", updateSelectedProps);
      canvas.off("selection:cleared", resetState);
    };
  }, []);

  useEffect(() => {
    const canvas = fabricCanvas.current;

    const handleWheel = (opt) => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom = zoom - delta / 300;
      zoom = Math.max(0.2, Math.min(zoom, 5));
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    };

    canvas.on("mouse:wheel", handleWheel);

    return () => {
      canvas.off("mouse:wheel", handleWheel);
    };
  }, []);

  useEffect(() => {
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    const handleTextboxEditing = (e) => {
      const textbox = e.target;

      if (textbox && textbox.type === "textbox") {
        textbox.initDimensions();
        textbox.setCoords();
        canvas.requestRenderAll();
      }
    };

    canvas.on("text:changed", handleTextboxEditing);
    canvas.on("text:editing:entered", handleTextboxEditing);

    return () => {
      canvas.off("text:changed", handleTextboxEditing);
      canvas.off("text:editing:entered", handleTextboxEditing);
    };
  }, []);

  useEffect(() => {
    if (document.fonts) {
      document.fonts.ready.then(() => {
        const canvas = fabricCanvas.current;
        if (canvas) {
          canvas.getObjects().forEach((obj) => {
            if (obj.type === "textbox") {
              obj.initDimensions();
              obj.setCoords();
            }
          });
          canvas.requestRenderAll();
        }
      });
    }
  }, []);

  useEffect(() => {
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    const handleTextboxResize = (e) => {
      const obj = e.target;

      if (obj && obj.type === "textbox") {
        if (obj.scaleX !== 1) {
          const newWidth = obj.width * obj.scaleX;
          obj.set({
            width: newWidth,
            scaleX: 1,
            scaleY: 1,
          });
        }

        obj.initDimensions();
        obj.setCoords();
        canvas.requestRenderAll();

        setTimeout(() => {
          obj.initDimensions();
          obj.setCoords();
          canvas.requestRenderAll();
        }, 10);
      }
    };

    canvas.on("object:scaling", handleTextboxResize);
    canvas.on("object:modified", handleTextboxResize);

    return () => {
      canvas.off("object:scaling", handleTextboxResize);
      canvas.off("object:modified", handleTextboxResize);
    };
  }, []);

  // Keyboard shortcuts: Delete / Backspace and Ctrl/Cmd + D (duplicate)
  useEffect(() => {
    const reSizePrefixes = {
      selectable: true,
      evented: true,
      hasControls: true,
      lockScalingFlip: true,
      lockUniScaling: true,
      objectCaching: false,
      borderColor: "black",
      cornerColor: "black",
      cornerStrokeColor: "black",
      // Add a fixed padding so duplicated objects keep a visible gap
      // between the shape and the resize/selection controls.
      padding: 12,
      cornerStyle: "rect",
    };
    const visibilityOptions = {
      mt: true, // middle top
      mb: true, // middle bottom
      ml: true, // middle left
      mr: true, // middle right
      tl: true, // top-left
      tr: true, // top-right
      bl: true, // bottom-left
      br: true, // bottom-right
      mtr: true, // rotation control
    };
    const onKeyDown = (e) => {
      // ignore when typing in inputs/textareas/contenteditable
      const tag = (e.target && e.target.tagName) || "";
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target && e.target.isContentEditable);
      if (isEditable) return;

      const canvas = fabricCanvas.current;
      if (!canvas) return;

      const active = canvas.getActiveObject();

      // Arrow keys => move active object
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        if (!active || active._undeletable || active.id === "backgroundImage")
          return;

        // Don't move if user is editing text inside a textbox
        if (active.type === "textbox" && active.isEditing) return;

        e.preventDefault();

        // Movement step: 1px normal, 10px with Shift key
        const step = e.shiftKey ? 10 : 1;

        switch (e.key) {
          case "ArrowUp":
            active.set({ top: active.top - step });
            break;
          case "ArrowDown":
            active.set({ top: active.top + step });
            break;
          case "ArrowLeft":
            active.set({ left: active.left - step });
            break;
          case "ArrowRight":
            active.set({ left: active.left + step });
            break;
        }

        active.setCoords();
        canvas.requestRenderAll();
        return;
      }

      // Delete / Backspace => remove active object
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (active && !active._undeletable) {
          canvas.remove(active);
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          resetState();
        }
        return;
      }

      // Ctrl/Cmd + D => duplicate active object
      if ((e.ctrlKey || e.metaKey) && e.key && e.key.toLowerCase() === "d") {
        e.preventDefault();
        const active = canvas.getActiveObject();
        if (!active) return console.log("No active object to duplicate");
        if (active._undeletable || active.id === "backgroundImage") return;

        // Check if multiple objects are selected
        console.log(
          "🔍 Checking object type:",
          active.type,
          "Is ActiveSelection?",
          active.type?.toLowerCase() === "activeselection"
        );

        // Block duplication for multi-select (activeSelection or ActiveSelection)
        if (active.type && active.type.toLowerCase() === "activeselection") {
          console.log("⛔ Multi-select detected - blocking duplication");
          alert(
            "Cannot duplicate multiple objects at once. Please select a single object."
          );
          return;
        }

        console.log("Attempting to duplicate active object", {
          type: active.type,
          id: active.id,
          left: active.left,
          top: active.top,
        });

        // Handle textbox duplication separately to avoid raster-crop fallback
        if (active.type === "textbox") {
          try {
            const text = active.text || "";

            // Manually copy only the properties we need (avoid getters/setters)
            const duplicatedTextbox = new fabric.Textbox(text, {
              left: (active.left || 0) + 10,
              top: (active.top || 0) + 10,
              fontSize: active.fontSize,
              fontFamily: active.fontFamily,
              fontWeight: active.fontWeight,
              fill: active.fill,
              width: active.width,
              textAlign: active.textAlign,

              // Shadow properties
              shadow: active.shadow
                ? {
                    color: active.shadow.color,
                    blur: active.shadow.blur,
                    offsetX: active.shadow.offsetX,
                    offsetY: active.shadow.offsetY,
                  }
                : null,

              // Custom textbox properties
              textBoxFillColor: active.textBoxFillColor,
              textBoxFillColorTransparent: active.textBoxFillColorTransparent,
              textBoxOpacity: active.textBoxOpacity,
              isDropShadowTransparent: active.isDropShadowTransparent,
              textBoxBorderColor: active.textBoxBorderColor,
              textBoxBorderColorTransparent:
                active.textBoxBorderColorTransparent,
              textBoxBorderWidth: active.textBoxBorderWidth,

              // Control properties
              selectable: true,
              evented: true,
              hasControls: true,
              lockScalingFlip: true,
              lockScalingY: true,
              lockUniScaling: true,
              objectCaching: false,
              statefullCache: false,
              noScaleCache: true,
              splitByGrapheme: true,
              borderColor: "black",
              cornerColor: "black",
              cornerStrokeColor: "black",
              editable: true,
            });

            // Set control visibility (only horizontal resize)
            duplicatedTextbox.setControlsVisibility({
              mt: false,
              mb: false,
              ml: true,
              mr: true,
              tl: false,
              tr: false,
              bl: false,
              br: false,
              mtr: true,
            });

            canvas.add(duplicatedTextbox);
            duplicatedTextbox.setCoords();
            canvas.setActiveObject(duplicatedTextbox);
            canvas.requestRenderAll();

            console.log("✅ Textbox duplicated successfully");
            return;
          } catch (textErr) {
            console.error("❌ Textbox duplication failed:", textErr);
            return;
          }
        }

        // 1) Try fabric clone API
        try {
          if (typeof active.clone === "function") {
            active.clone((cloned) => {
              if (!cloned) return console.warn("clone() returned falsy");
              if (cloned.customId) delete cloned.customId;
              if (cloned.id) delete cloned.id;
              if (cloned._undeletable) delete cloned._undeletable;
              cloned.set({
                left: (active.left || 0) + 10,
                top: (active.top || 0) + 10,
                selectable: true,
                evented: true,
              });
              cloned.set(reSizePrefixes);
              cloned.setControlsVisibility(visibilityOptions);
              canvas.add(cloned);
              cloned.setCoords();
              canvas.setActiveObject(cloned);
              canvas.requestRenderAll();
              console.log("Cloned object added to canvas");
            });
            return;
          }
        } catch (err) {
          console.warn("clone() failed", err);
        }

        // 2) Try to reconstruct from serialized data (handle path specially)
        try {
          const objData = active.toObject(["customId", "id", "_undeletable"]);
          delete objData.customId;
          delete objData.id;
          delete objData._undeletable;

          const type = (objData.type || active.type || "").toLowerCase();

          // Path reconstruction
          if (type === "path" || objData.path) {
            try {
              const pathData = objData.path;
              const reconstructed = new fabric.Path(pathData, objData);
              reconstructed.set({
                left: (active.left || 0) + 10,
                top: (active.top || 0) + 10,
                selectable: true,
                evented: true,
              });
              reconstructed.set(reSizePrefixes);
              reconstructed.setControlsVisibility(visibilityOptions);
              canvas.add(reconstructed);
              reconstructed.setCoords();
              canvas.setActiveObject(reconstructed);
              canvas.requestRenderAll();
              return;
            } catch (pErr) {
              console.warn("Path reconstruction failed", pErr);
            }
          }

          // Simple shapes
          if (type === "rect") {
            const reconstructed = new fabric.Rect(objData);
            reconstructed.set({
              left: (active.left || 0) + 10,
              top: (active.top || 0) + 10,
              selectable: true,
              evented: true,
            });
            reconstructed.set(reSizePrefixes);
            reconstructed.setControlsVisibility(visibilityOptions);
            canvas.add(reconstructed);
            reconstructed.setCoords();
            canvas.setActiveObject(reconstructed);
            canvas.requestRenderAll();
            return;
          }
          if (type === "circle") {
            const reconstructed = new fabric.Circle(objData);
            reconstructed.set({
              left: (active.left || 0) + 10,
              top: (active.top || 0) + 10,
              selectable: true,
              evented: true,
            });
            reconstructed.set(reSizePrefixes);
            reconstructed.setControlsVisibility(visibilityOptions);
            canvas.add(reconstructed);
            reconstructed.setCoords();
            canvas.setActiveObject(reconstructed);
            canvas.requestRenderAll();
            return;
          }
          if (type === "triangle") {
            const reconstructed = new fabric.Triangle(objData);
            reconstructed.set({
              left: (active.left || 0) + 10,
              top: (active.top || 0) + 10,
              selectable: true,
              evented: true,
            });
            reconstructed.set(reSizePrefixes);
            reconstructed.setControlsVisibility(visibilityOptions);
            canvas.add(reconstructed);
            reconstructed.setCoords();
            canvas.setActiveObject(reconstructed);
            canvas.requestRenderAll();
            return;
          }
          // if (type === "textbox") {
          //   const text = active.text || objData.text || "";
          //   const reconstructed = new fabric.Textbox(text, objData);
          //   reconstructed.set({
          //     left: (active.left || 0) + 10,
          //     top: (active.top || 0) + 10,
          //     selectable: true,
          //     evented: true,
          //   });
          //   canvas.add(reconstructed);
          //   reconstructed.setCoords();
          //   canvas.setActiveObject(reconstructed);
          //   canvas.requestRenderAll();
          //   return;
          // }
        } catch (serErr) {
          console.warn("Serialization-based reconstruction failed", serErr);
        }

        // 3) Raster-crop fallback: draw the object's bounding box from the full canvas image
        try {
          const bounds = active.getBoundingRect(true, true);
          const canvasDataUrl = canvas.toDataURL({ format: "png" });
          const srcImg = new Image();
          srcImg.onload = () => {
            try {
              const cropCanvas = document.createElement("canvas");
              cropCanvas.width = Math.max(1, Math.round(bounds.width));
              cropCanvas.height = Math.max(1, Math.round(bounds.height));
              const ctx = cropCanvas.getContext("2d");
              ctx.drawImage(
                srcImg,
                Math.round(bounds.left),
                Math.round(bounds.top),
                Math.round(bounds.width),
                Math.round(bounds.height),
                0,
                0,
                Math.round(bounds.width),
                Math.round(bounds.height)
              );
              const croppedData = cropCanvas.toDataURL();
              const imgEl = new Image();
              imgEl.onload = () => {
                const imgObj = new fabric.Image(imgEl, {
                  left: (active.left || 0) + 10,
                  top: (active.top || 0) + 10,
                  selectable: true,
                  evented: true,
                });
                // Apply the same resize/control styling (including padding)
                // so raster-cropped duplicates also get the static gap.
                if (imgObj.set) {
                  imgObj.set(reSizePrefixes);
                }
                canvas.add(imgObj);
                imgObj.setCoords();
                canvas.setActiveObject(imgObj);
                canvas.requestRenderAll();
                console.log("Raster-cropped duplicate added to canvas");
              };
              imgEl.onerror = (e) =>
                console.error("Failed to load cropped image", e);
              imgEl.src = croppedData;
            } catch (cropErr) {
              console.error("Raster-crop duplication failed", cropErr);
            }
          };
          srcImg.onerror = (e) =>
            console.error("Failed to load canvas image for cropping", e);
          srcImg.src = canvasDataUrl;
        } catch (rErr) {
          console.error("Raster-crop fallback failed", rErr);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // --------------------- FUNCTIONS ---------------------

  const addToCanvas = (obj) => {
    const canvas = fabricCanvas.current;

    if (!obj) return;

    // Set standard resizable behavior
    obj.set({
      selectable: true,
      evented: true,
      hasControls: true,
      lockScalingFlip: true,
      lockUniScaling: true,
      objectCaching: false,
      borderColor: "black",
      cornerColor: "black",
      cornerStrokeColor: "black",
      cornerStyle: "rect",
    });

    // Optional: Ensure bounding box doesn't look weird
    if (obj.setControlsVisibility) {
      obj.setControlsVisibility({
        mt: true, // middle top
        mb: true, // middle bottom
        ml: true, // middle left
        mr: true, // middle right
        tl: true, // top-left
        tr: true, // top-right
        bl: true, // bottom-left
        br: true, // bottom-right
        mtr: true, // rotation control
      });
    }

    canvas.add(obj);
    obj.setCoords();
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
  };

  const setPropRecursive = (obj, prop, value) => {
    if (!obj) return;

    if (obj._objects && Array.isArray(obj._objects)) {
      obj._objects.forEach((child) => setPropRecursive(child, prop, value));
    }

    if (obj.set && obj.type !== "image") {
      obj.set(prop, value);
    }
  };

  const openSVGPicker = () => {
    svgInputRef.current?.click();
  };

  const handleSVGFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text(); // read SVG as text
      await addSVGFromString(text);
    } catch (err) {
      console.error("Failed to load SVG:", err);
    } finally {
      // reset input so selecting the same file again still triggers change
      e.target.value = "";
    }
  };

  const addSVGFromString = async (svgString) => {
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    try {
      // Fabric v6: returns { objects, options }
      const { objects, options } = await fabric.loadSVGFromString(svgString);

      // Group the SVG into a single selectable object
      // (works in Fabric v6 via fabric.util.groupSVGElements)
      const group = fabric.util.groupSVGElements(objects, options);

      // ---- Scale-to-fit (keep within ~80% of canvas) ----
      const maxW = canvas.getWidth() * 0.15;
      const maxH = canvas.getHeight() * 0.15;
      // getBoundingRect(true, true) includes stroke & transforms
      const bounds = group.getBoundingRect(true, true);
      const scale = Math.min(maxW / bounds.width, maxH / bounds.height, 1);
      if (Number.isFinite(scale) && scale > 0) {
        group.scale(scale);
      }

      // Center it
      // After scaling, recompute bounds to center properly
      const b2 = group.getBoundingRect(true, true);
      group.set({
        left: (canvas.getWidth() - b2.width) / 2 - (b2.left - group.left),
        top: (canvas.getHeight() - b2.height) / 2 - (b2.top - group.top),
      });

      // Add to canvas
      addToCanvas(group);
      console.log("Error while generating SVG");
    } catch (e) {
      console.error("Error while generating SVG:", e);
    } finally {
      setShowShapeSelector(false);
    }
  };

  const resetState = () => {
    setShowShapeSelector(false);
    setSelectedTextProps(null);
    setSelectedShapeProps(null);
  };
  // for mao background image interaction
  const toggleBackgroundInteraction = (isEnabled) => {
    const canvas = fabricCanvas.current;
    console.log(canvas, "toggle interactable");
    const bgImage = canvas
      .getObjects()
      .find((obj) => obj.id === "backgroundImage");
    console.log(bgImage, " Bg-Image");
    if (!bgImage) return;

    bgImage.set({
      selectable: isEnabled,
      evented: isEnabled,
      hasControls: isEnabled,
      hasBorders: isEnabled,
      hoverCursor: isEnabled ? "move" : "default",
    });

    canvas.requestRenderAll();
  };
  const removeImageByKey = (canvas, key) => {
    const existing = canvas.getObjects().find((obj) => obj.customId === key);
    if (existing) {
      canvas.remove(existing);
    }
  };

  const toggleImageVisibilityByKey = (key, visible) => {
    const canvas = fabricCanvas.current;
    if (!canvas || !key) return;

    const target = canvas.getObjects().find((obj) => obj.customId === key);
    if (target) {
      target.set("visible", visible);

      // 🔒 Deselect if hiding the currently selected object
      if (!visible && canvas.getActiveObject() === target) {
        canvas.discardActiveObject();
      }

      canvas.requestRenderAll();
    }
  };

  const handleFabricImageUpload = async (dataURL, key = "") => {
    try {
      const imgEl = await fabric.util.loadImage(dataURL);
      const canvas = fabricCanvas.current;
      canvas.preserveObjectStacking = true;

      const fabricImg = new fabric.Image(imgEl);

      if (key) {
        // Remove existing image with same key
        removeImageByKey(canvas, key);

        // Proceed to add new one
        const fixedWidth = 200;
        const fixedHeight = 200;

        const scaleX = fixedWidth / imgEl.naturalWidth;
        const scaleY = fixedHeight / imgEl.naturalHeight;

        fabricImg.set({
          left: canvas.getWidth() - fixedWidth - 10,
          top: 10,
          scaleX,
          scaleY,
          selectable: true,
          evented: true,
          hasControls: true,
          hoverCursor: "move",
          customId: key,
          borderColor: "black",
          cornerColor: "black",
          cornerStrokeColor: "black",
        });

        fabricImg.set("visible", false);
      } else {
        const imgWidth = fabricImg.width;
        const imgHeight = fabricImg.height;

        const maxFrameWidth = 1000;
        const maxFrameHeight = 950;

        const scale = Math.min(
          maxFrameWidth / imgWidth,
          maxFrameHeight / imgHeight,
          1
        );

        const newFrameWidth = Math.round(imgWidth * scale);
        const newFrameHeight = Math.round(imgHeight * scale);

        setFrameSize({
          width: newFrameWidth + 20,
          height: newFrameHeight + 20,
        });
        canvas.setWidth(newFrameWidth);
        canvas.setHeight(newFrameHeight);
        canvas.calcOffset();
        canvas.renderAll();

        fabricImg.set({
          left: (newFrameWidth - imgWidth * scale) / 2,
          top: (newFrameHeight - imgHeight * scale) / 2,
          scaleX: scale,
          scaleY: scale,
          _undeletable: true,
          selectable: false,
          evented: false,
          hasControls: false,
          hoverCursor: "default",
          id: "backgroundImage",
        });

        canvas.clear();
      }
      canvas.add(fabricImg);
      fabricImg.setCoords();
      canvas.setActiveObject(fabricImg);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      // ✅ Return a promise so it can be awaited
      return Promise.resolve();
    } catch (err) {
      console.error("❌ Image load failed", err);
      return Promise.reject(err);
    }
  };

  const downloadImage = async (format = "pdf") => {
    const canvas = fabricCanvas.current;
    const frameElement = frameRef.current;

    if (!canvas) return;

    const scaleFactor = 2;
    const width = frameSize.width;
    const height = frameSize.height;

    try {
      // 1. Export high-res canvas
      const scaledCanvas = canvas.toCanvasElement(scaleFactor);

      // 2. Export frame DOM as image (also scaled)
      const frameImageUrl = await toPng(frameElement, {
        pixelRatio: scaleFactor,
        width: width * scaleFactor,
        height: height * scaleFactor,
      });

      // 3. Composite canvas + frame image onto final canvas
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = width * scaleFactor;
      finalCanvas.height = height * scaleFactor;
      const ctx = finalCanvas.getContext("2d");

      // Draw canvas first
      ctx.drawImage(scaledCanvas, 0, 0);

      // Load and draw frame on top
      const frameImg = new Image();
      frameImg.onload = () => {
        ctx.drawImage(frameImg, 0, 0);

        // 4. Generate image or PDF based on format
        if (format === "pdf") {
          const imgData = finalCanvas.toDataURL("image/jpeg", 1.0); // jsPDF prefers JPEG
          const pdf = new jsPDF({
            orientation: orientation === "portrait" ? "portrait" : "landscape",
            unit: "px",
            format: [finalCanvas.width, finalCanvas.height],
          });
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            0,
            finalCanvas.width,
            finalCanvas.height
          );
          pdf.save("framed-highres.pdf");
        } else {
          // 'png' or 'jpeg'
          const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
          const finalUrl = finalCanvas.toDataURL(mimeType);

          const link = document.createElement("a");
          link.href = finalUrl;
          link.download = `framed-highres.${format}`;
          link.click();
        }

        setIsLoading(false);
      };

      frameImg.onerror = (e) => {
        console.error("Failed to load frame image", e);
        setIsLoading(false);
      };

      frameImg.src = frameImageUrl;
    } catch (err) {
      console.error("Image export failed:", err);
      setIsLoading(false);
    }
  };

  const addText = () => {
    resetState();
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    const text = new fabric.Textbox("Your text here", {
      left: 100,
      top: 100,
      fontSize: 24,
      fill: "#000000",
      editable: true,
      borderColor: "black",
      cornerColor: "black",
      cornerStrokeColor: "black",
      width: 200,

      // Cursor fix properties
      objectCaching: false,
      statefullCache: false,
      noScaleCache: true,
      splitByGrapheme: true,

      // Resize control
      lockScalingY: true,
      lockUniScaling: true,

      shadow: {
        color: "rgba(0,0,0,0)",
        blur: 3,
        offsetX: 10,
        offsetY: 10,
      },
      borderColor: "#000000",
      borderWidth: 2,
      selectable: true,
      hasControls: true,
      evented: true,
      lockScalingFlip: true,
    });

    // Editing events
    text.on("editing:entered", function () {
      this.hiddenTextarea.focus();
      this.initDimensions();
      canvas.requestRenderAll();
    });

    text.on("editing:exited", function () {
      this.initDimensions();
      canvas.requestRenderAll();
    });

    text.on("changed", function () {
      this.setCoords();
      canvas.requestRenderAll();
    });

    text.on("scaling", function () {
      const newWidth = this.width * this.scaleX;
      this.set({
        width: newWidth,
        scaleX: 1,
        scaleY: 1,
      });
      this.initDimensions();
      this.setCoords();
    });

    text.on("modified", function () {
      const newWidth = this.width * this.scaleX;
      this.set({
        width: newWidth,
        scaleX: 1,
        scaleY: 1,
      });
      this.initDimensions();
      this.setCoords();
      canvas.requestRenderAll();
    });

    // Optional: Only show horizontal resize
    text.setControlsVisibility({
      mt: false,
      mb: false,
      ml: true,
      mr: true,
      tl: false,
      tr: false,
      bl: false,
      br: false,
      mtr: true,
    });

    addToCanvas(text);
  };

  const addCustomShape = async (key) => {
    resetState();
    const canvas = fabricCanvas.current;
    const svgString = shapes[key].svg;
    try {
      // const { objects, options } = await fabric.loadSVGFromString(svgString);
      // objects.forEach((obj) => {
      //   obj.set({
      //     fill: "#000000",
      //     stroke: "#000000",
      //     strokeWidth: 2,
      //     opacity: 1,
      //   });
      // });
      // const group = fabric.util.groupSVGElements(objects, options);
      // const maxW = canvas.getWidth() * 0.15;
      // const maxH = canvas.getHeight() * 0.15;
      // const bounds = group.getBoundingRect(true, true);
      // const scale = Math.min(maxW / bounds.width, maxH / bounds.height, 1);
      // if (Number.isFinite(scale) && scale > 0) {
      //   group.scale(scale);
      // }

      // // 🎯 Center on canvas
      // const b2 = group.getBoundingRect(true, true);
      // group.set({
      //   left: (canvas.getWidth() - b2.width) / 2 - (b2.left - group.left),
      //   top: (canvas.getHeight() - b2.height) / 2 - (b2.top - group.top),
      //   selectable: true,
      //   hasControls: true,
      //   evented: true,
      //   lockScalingFlip: true,
      // });
      const { objects, options } = await fabric.loadSVGFromString(svgString);
      const scaleFactor = 0.15;
      objects.forEach((obj) => obj.scale(scaleFactor));
      const icon = fabric.util.groupSVGElements(objects, options);
      // options.fill = "#000000";
      // options.stroke = "#000000";
      // options.strokeWidth = 10;
      options.opacity = 1;
      icon.set(options);
      icon.set({
        left: 100,
        top: 100,
        selectable: true,
        hasControls: true,
        evented: true,
        lockScalingFlip: true,
      });
      addToCanvas(icon);
    } catch (e) {
      console.log("Error while generating Custom Shpae", e);
    } finally {
      // resetState();
    }
  };

  const addImageToCanvas = async (imageURL) => {
    console.log("Received imageURL:", imageURL);

    const canvas = fabricCanvas.current;
    console.log(canvas, "canvas");
    if (!canvas || !imageURL) {
      console.log("Canvas or imageURL is missing.");
      return;
    }

    // Handle only base64 images
    if (!imageURL.startsWith("data:image/")) {
      console.log("Only Base64 images are supported by this function.");
      return;
    }

    try {
      // Load base64 image using fabric.util.loadImage
      const imgEl = await fabric.util.loadImage(imageURL);
      if (!imgEl) {
        throw new Error("Image could not be loaded.");
      }

      const fabricImg = new fabric.Image(imgEl, {
        selectable: true,
        evented: true,
        objectCaching: false,
      });

      const imgWidth = fabricImg.width || imgEl.naturalWidth;
      const imgHeight = fabricImg.height || imgEl.naturalHeight;

      console.log("Original Image Size:", imgWidth, imgHeight);

      // Your original max frame size
      const maxFrameWidth = 300;
      const maxFrameHeight = 250;

      // Calculate scale to fit image in max frame size while keeping aspect ratio
      const scale = Math.min(
        maxFrameWidth / imgWidth,
        maxFrameHeight / imgHeight,
        1 // Do not upscale
      );

      const newFrameWidth = imgWidth * scale;
      const newFrameHeight = imgHeight * scale;

      // Center the image in the canvas
      fabricImg.set({
        scaleX: scale,
        scaleY: scale,
        left: (canvas.getWidth() - newFrameWidth) / 2,
        top: (canvas.getHeight() - newFrameHeight) / 2,
        selectable: true,
        hasControls: true,
        evented: true,
        lockScalingFlip: true,
        borderColor: "black",
        cornerColor: "black",
        cornerStrokeColor: "black",
      });

      addToCanvas(fabricImg);

      console.log("✅ Base64 image added to canvas.");
    } catch (err) {
      console.log("❌ Failed to load image:", err);
    }
  };

  const updateActiveTextProperty = (prop, value) => {
    const canvas = fabricCanvas.current;
    const active = canvas.getActiveObject();
    if (active && active.type === "textbox") {
      active.set(prop, value);
      active.initDimensions();
      active.setCoords();
      canvas.requestRenderAll();
      if (["fontSize", "fontFamily", "fontWeight"].includes(prop)) {
        setTimeout(() => {
          active.initDimensions();
          active.setCoords();
          canvas.requestRenderAll();
        }, 10);
      }
    }
  };

  const updateActiveShapeProperty = (prop, value) => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (!active || active.type === "textbox") return;

    if (["fill", "stroke", "strokeWidth", "shadow"].includes(prop)) {
      setPropRecursive(active, prop, value);
    } else if (prop === "scale") {
      // Apply uniform scaling
      active.set({
        scaleX: value,
        scaleY: value,
        selectable: true,
        evented: true,
      });
      active.setCoords();
    } else {
      active.set(prop, value);
    }

    canvas.requestRenderAll();
  };

  const updateSelectedObjectScale = (key, value) => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (!active) return;

    // Decide what to update
    if (key === "scale") {
      // Uniform scaling
      active.set({
        scaleX: value,
        scaleY: value,
      });

      setSelectedObjectScale({
        scale: value,
        scaleX: value,
        scaleY: value,
      });
    } else if (key === "scaleX") {
      active.set("scaleX", value);

      setSelectedObjectScale((prev) => ({
        ...prev,
        scaleX: value,
      }));
    } else if (key === "scaleY") {
      active.set("scaleY", value);

      setSelectedObjectScale((prev) => ({
        ...prev,
        scaleY: value,
      }));
    }

    active.setCoords();
    canvas.requestRenderAll();
  };

  const zoomIn = () => {
    const canvas = fabricCanvas.current;
    let zoom = canvas.getZoom();
    zoom = Math.min(zoom * 1.1, 5); // Max zoom level = 5x
    canvas.setZoom(zoom);
    centerViewport(canvas, zoom);
  };

  const zoomOut = () => {
    const canvas = fabricCanvas.current;
    let zoom = canvas.getZoom();
    zoom = Math.max(zoom / 1.1, 0.2); // Min zoom level = 0.2x
    canvas.setZoom(zoom);
    centerViewport(canvas, zoom);
  };

  const resetZoom = () => {
    const canvas = fabricCanvas.current;
    canvas.setZoom(1);
    centerViewport(canvas, 1);
  };

  const centerViewport = (canvas, zoom) => {
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    canvas.viewportTransform[4] = canvasWidth / 2 - (canvasWidth / 2) * zoom;
    canvas.viewportTransform[5] = canvasHeight / 2 - (canvasHeight / 2) * zoom;
    canvas.requestRenderAll();
  };

  const deleteActiveObject = () => {
    const canvas = fabricCanvas.current;
    if (!canvas) return;

    const active = canvas.getActiveObject();
    if (active) {
      if (active._undeletable) {
        console.log("This object cannot be deleted.");
        return;
      }
      canvas.remove(active);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      resetState();
    }
  };

  const bringForward = () => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (active) {
      canvas.bringObjectForward(active);
      canvas.requestRenderAll();
    }
  };

  const sendBackward = () => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (active) {
      canvas.sendObjectBackwards(active);
      canvas.requestRenderAll();
    }
  };

  const bringToFront = () => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (active) {
      canvas.bringObjectToFront(active);
      canvas.requestRenderAll();
    }
  };

  const sendToBack = () => {
    const canvas = fabricCanvas.current;
    const active = canvas?.getActiveObject();
    if (active && active._undeletable) {
      // ✅ keep background safe
      canvas.sendObjectToBack(active);
      canvas.requestRenderAll();
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        bgcolor: "#fcfcfc",
        color: "white",
        fontFamily: "Roboto, sans-serif",
      }}
    >
      {/* Mobile menu button */}
      {isMobile && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 1201,
            color: "gray",
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar as permanent on desktop, Drawer on mobile */}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={!drawerOpen}
          onClose={toggleDrawer}
          sx={{
            "& .MuiDrawer-paper": { width: 420 },
          }}
        >
          <Sidebar
            activeLogo={activeLogo}
            uploadedLogos={uploadedLogos}
            setUploadedLogos={setUploadedLogos}
            setPreview={setPreview}
            preview={preview}
            setSelectedObjectScale={setSelectedObjectScale}
            updateSelectedObjectScale={updateSelectedObjectScale}
            selectedObjectScale={selectedObjectScale}
            exportFormat={exportFormat}
            setExportFormat={setExportFormat}
            leagendUrl={leagendUrl}
            toggleImageVisibilityByKey={toggleImageVisibilityByKey}
            isLeagend={isLeagend}
            setIsLeagend={setIsLeagend}
            toggleBackgroundInteraction={toggleBackgroundInteraction}
            addImageToCanvas={addImageToCanvas}
            setFrameText={setFrameText}
            frameText={frameText}
            setFrameDesign={setFrameDesign}
            setIsLoading={setIsLoading}
            frameDesign={frameDesign}
            canvas={fabricCanvas.current}
            addText={addText}
            setShowShapeSelector={setShowShapeSelector}
            downloadImage={downloadImage}
            deleteActiveObject={deleteActiveObject}
            bringForward={bringForward}
            sendBackward={sendBackward}
            bringToFront={bringToFront}
            sendToBack={sendToBack}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            resetZoom={resetZoom}
            showShapeSelector={showShapeSelector}
            openSVGPicker={openSVGPicker}
            addSVGFromString={addSVGFromString}
            svgInputRef={svgInputRef}
            handleSVGFile={handleSVGFile}
            shapes={shapes}
            addCustomShape={addCustomShape}
            selectedTextProps={selectedTextProps}
            setSelectedTextProps={setSelectedTextProps}
            updateActiveTextProperty={updateActiveTextProperty}
            selectedShapeProps={selectedShapeProps}
            setSelectedShapeProps={setSelectedShapeProps}
            updateActiveShapeProperty={updateActiveShapeProperty}
          />
        </Drawer>
      ) : (
        <Sidebar
          activeLogo={activeLogo}
          setUploadedLogos={setUploadedLogos}
          uploadedLogos={uploadedLogos}
          setPreview={setPreview}
          preview={preview}
          setSelectedObjectScale={setSelectedObjectScale}
          updateSelectedObjectScale={updateSelectedObjectScale}
          selectedObjectScale={selectedObjectScale}
          exportFormat={exportFormat}
          setExportFormat={setExportFormat}
          leagendUrl={leagendUrl}
          toggleImageVisibilityByKey={toggleImageVisibilityByKey}
          isLeagend={isLeagend}
          setIsLeagend={setIsLeagend}
          toggleBackgroundInteraction={toggleBackgroundInteraction}
          addImageToCanvas={addImageToCanvas}
          setFrameText={setFrameText}
          frameText={frameText}
          setFrameDesign={setFrameDesign}
          setIsLoading={setIsLoading}
          frameDesign={frameDesign}
          canvas={fabricCanvas.current}
          addText={addText}
          setShowShapeSelector={setShowShapeSelector}
          downloadImage={downloadImage}
          deleteActiveObject={deleteActiveObject}
          bringForward={bringForward}
          sendBackward={sendBackward}
          bringToFront={bringToFront}
          sendToBack={sendToBack}
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetZoom={resetZoom}
          showShapeSelector={showShapeSelector}
          openSVGPicker={openSVGPicker}
          addSVGFromString={addSVGFromString}
          svgInputRef={svgInputRef}
          handleSVGFile={handleSVGFile}
          shapes={shapes}
          addCustomShape={addCustomShape}
          selectedTextProps={selectedTextProps}
          setSelectedTextProps={setSelectedTextProps}
          updateActiveTextProperty={updateActiveTextProperty}
          selectedShapeProps={selectedShapeProps}
          setSelectedShapeProps={setSelectedShapeProps}
          updateActiveShapeProperty={updateActiveShapeProperty}
        />
      )}

      {/* Main Canvas Area */}
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "#252525",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderLeft: "2px solid #333",
          p: 2,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            height: frameSize.height || 500,
            width: frameSize.width || 800,
            position: "relative",
          }}
          onClick={() => setActiveLogo("")} // <-- clear active when clicking outside logos
        >
          <div
            ref={frameRef}
            style={{
              height: frameSize.height || 500,
              width: frameSize.width || 800,
              position: "absolute",
            }}
          >
            {uploadedLogos.length > 0 ? (
              uploadedLogos.map((obj, index) => (
                <DargableLogo
                  divSize={obj?.size ? obj.size / 2 : 100}
                  activeLogo={activeLogo}
                  height={frameSize.height}
                  width={frameSize.width}
                  logo={
                    obj.src ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          flexDirection: "column",
                          cursor: "move",
                          alignItems: "end",
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // prevent outer click handler
                          setActiveLogo(obj.src);
                        }}
                      >
                        {activeLogo === obj.src && (
                          <CloseSharp
                            onClick={(e) => {
                              e.stopPropagation(); // prevent clearing activeLogo by parent
                              setActiveLogo("");
                              setUploadedLogos((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                            }}
                            style={{
                              position: "absolute",
                              top: -20,
                              right: -10,
                              color: "red",
                              cursor: "pointer",
                              zIndex: 2,
                            }}
                          />
                        )}
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveLogo(obj.src);
                          }}
                          src={obj.src}
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "contain",
                          }}
                          alt="logo"
                          draggable={false}
                        />
                      </div>
                    ) : (
                      <></>
                    )
                  }
                />
              ))
            ) : (
              <></>
            )}

            <CustomFrames
              frameDesign={frameDesign}
              frameText={frameText}
              orientation={orientation}
            />
          </div>
          <div
            style={{
              position: "relative",
              border: "10px solid #333",
              boxShadow: "0 0 10px rgba(0,0,0,0.8)",
              display: "inline-block",
              lineHeight: 0,
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
              }}
            />
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default ImageEditor;
